import { FrostedBackdropTarget } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { PrimaryControlsStripToolbar } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import { Link } from "@-/com.npmjs.expo-router";
import { Pressable, ScrollView, View } from "@-/com.npmjs.react-native";
import {
  HeaderLegendList,
  HeaderScrollView,
} from "@-/dev.improve.haniverse.components.ui.header-scroll";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Input } from "@-/dev.improve.haniverse.components.ui.input";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  DocumentLayoutSkeleton,
  SuspensePageBoundary,
} from "@-/dev.improve.haniverse.components.ui.suspense-page";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { PrimaryControlsStripTitle } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import { PrimaryControlsStripBack } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import { useBrowseLevelsStudyGraphemesQuery } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels.navigator";
import { useMemo, useState } from "react";
import { COOKBOOK_CHAPTERS, COOKBOOK_RECIPES, type CookbookRecipe } from "#cookbook-recipes";
import { CookbookAccessGate } from "#cookbook-access-gate";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";

type RecipeView = CookbookRecipe & {
  name: string;
  needed: string | null;
  page: number;
  progress: string;
  unlocked: boolean;
};
type ContentsRow =
  | { chapter: CookbookRecipe["chapter"]; kind: "chapter" }
  | { kind: "recipe"; recipe: RecipeView };

function RecipeContentsRow({ recipe }: { recipe: RecipeView }) {
  return (
    <Link
      asChild
      href={{ params: { recipe: recipe.id }, pathname: "/(nav)/(app)/cookbook/[recipe]" }}
    >
      <Pressable
        accessibilityLabel={`${recipe.english}, ${recipe.name}, ${recipe.progress}${recipe.needed === null ? "" : `, ${recipe.needed}`}, page ${recipe.page}`}
        accessibilityRole="link"
        className="mb-2 min-h-20 flex-row items-center gap-3 rounded-xs border border-border bg-background p-4 active:bg-accent"
        testID={`cookbook-recipe-${recipe.id}`}
      >
        <Icon
          className={recipe.unlocked ? "text-activity-3" : "text-muted-foreground"}
          name={recipe.unlocked ? "MdCheck" : "MdLockOutline"}
          size={20}
        />
        <View className="flex-1 gap-1">
          <Text className="text-lg font-semibold text-foreground">{recipe.name}</Text>
          <Text className="text-sm text-muted-foreground">{recipe.english}</Text>
          <Text className="text-xs text-muted-foreground">{recipe.progress}</Text>
          {recipe.needed === null ? null : (
            <Text className="text-sm text-foreground">{recipe.needed}</Text>
          )}
        </View>
        <Text className="text-sm text-muted-foreground">
          {String(recipe.page).padStart(3, "0")}
        </Text>
        <Icon className="text-muted-foreground" name="MdChevronRight" size={18} />
      </Pressable>
    </Link>
  );
}

function CookbookContents({
  progress,
  recipes,
}: {
  progress: string;
  recipes: readonly RecipeView[];
}) {
  const [search, setSearch] = useState("");
  const [chapter, setChapter] = useState<CookbookRecipe["chapter"] | null>(null);
  const { height: toolbarHeight } = useToolbarGridMetrics();
  const query = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      recipes.filter(
        (recipe) =>
          (chapter === null || recipe.chapter === chapter) &&
          `${recipe.english} ${recipe.simplified} ${recipe.traditional}`
            .toLowerCase()
            .includes(query),
      ),
    [chapter, query, recipes],
  );
  const rows = useMemo(
    () =>
      COOKBOOK_CHAPTERS.flatMap((name): ContentsRow[] => {
        const matches = filtered.filter((recipe) => recipe.chapter === name);
        return matches.length === 0
          ? []
          : [
              { chapter: name, kind: "chapter" },
              ...matches.map((recipe): ContentsRow => ({ kind: "recipe", recipe })),
            ];
      }),
    [filtered],
  );

  return (
    <View className="flex-1 bg-background" testID="cookbook-page">
      <PrimaryControlsStripTitle>Cookbook</PrimaryControlsStripTitle>
      <PrimaryControlsStripToolbar floating />
      <FrostedBackdropTarget className="flex-1">
        <HeaderLegendList
          className="flex-1"
          contentContainerClassName="px-5 pt-6 pb-10"
          data={rows}
          extraData={recipes}
          glassHeaderOverlap={toolbarHeight}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
          keyExtractor={(row) =>
            row.kind === "chapter" ? `chapter-${row.chapter}` : row.recipe.id
          }
          ListEmptyComponent={
            <View className="mx-auto w-full max-w-2xl gap-2 py-8">
              <Text className="text-lg font-semibold text-foreground">No recipes found</Text>
              <Text className="text-sm text-muted-foreground">
                Try another dish name or clear the filters.
              </Text>
            </View>
          }
          ListHeaderComponent={
            <View className="mx-auto mb-4 w-full max-w-2xl gap-4">
              <View className="gap-3 border-b border-border pb-5">
                <Text className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
                  A taste of Chinese
                </Text>
                <Text className="text-4xl font-bold text-foreground">Cookbook</Text>
                <Text className="text-base text-muted-foreground">
                  Learn the characters. Unlock the recipes.
                </Text>
                <Text className="text-sm text-muted-foreground">
                  Each recipe unlocks when every character in its Chinese name has been studied.
                  Turn to any page for a preview. Every dish stays in the table of contents,
                  including recipes still waiting to be unlocked.
                </Text>
                <Text className="text-sm font-semibold text-foreground">{progress}</Text>
              </View>
              <Text accessibilityRole="header" className="text-2xl font-semibold text-foreground">
                Table of contents
              </Text>
              <Input
                accessibilityLabel="Search recipes"
                autoCapitalize="none"
                autoCorrect={false}
                onChangeText={setSearch}
                placeholder="Search in English or Chinese"
                testID="cookbook-search"
                value={search}
              />
              <ScrollView
                contentContainerClassName="gap-2"
                horizontal
                keyboardShouldPersistTaps="handled"
                showsHorizontalScrollIndicator={false}
              >
                {[null, ...COOKBOOK_CHAPTERS].map((name) => (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityState={{ selected: chapter === name }}
                    className={
                      chapter === name
                        ? "min-h-11 justify-center rounded-full bg-foreground px-4"
                        : "min-h-11 justify-center rounded-full border border-border px-4"
                    }
                    key={name ?? "all"}
                    onPress={() => setChapter(name)}
                  >
                    <Text
                      className={
                        chapter === name
                          ? "text-sm font-semibold text-background"
                          : "text-sm text-foreground"
                      }
                    >
                      {name ?? "All chapters"}
                    </Text>
                  </Pressable>
                ))}
              </ScrollView>
              <View className="flex-row items-center justify-between gap-3">
                <Text accessibilityLiveRegion="polite" className="text-sm text-muted-foreground">
                  {filtered.length} {filtered.length === 1 ? "recipe" : "recipes"} ·{" "}
                  {chapter ?? `${COOKBOOK_CHAPTERS.length} chapters`}
                </Text>
                {query !== "" || chapter !== null ? (
                  <Pressable
                    accessibilityRole="button"
                    className="min-h-11 justify-center px-2"
                    onPress={() => {
                      setSearch("");
                      setChapter(null);
                    }}
                  >
                    <Text className="text-sm font-semibold text-foreground">Clear filters</Text>
                  </Pressable>
                ) : null}
              </View>
            </View>
          }
          renderItem={({ item }) => (
            <View className="mx-auto w-full max-w-2xl">
              {item.kind === "chapter" ? (
                <Text
                  accessibilityRole="header"
                  className="pt-5 pb-3 text-xs font-semibold tracking-widest text-muted-foreground uppercase"
                >
                  {String(COOKBOOK_CHAPTERS.indexOf(item.chapter) + 1).padStart(2, "0")} ·{" "}
                  {item.chapter}
                </Text>
              ) : (
                <RecipeContentsRow recipe={item.recipe} />
              )}
            </View>
          )}
          testID="cookbook-contents-list"
        />
      </FrostedBackdropTarget>
    </View>
  );
}

function CookbookContent({ recipeId }: { recipeId?: string }) {
  const { hanziScript } = useActorUser();
  const { height: toolbarHeight } = useToolbarGridMetrics();
  const { data: studiedCharacters, isError } = useBrowseLevelsStudyGraphemesQuery();
  const recipes = useMemo(
    (): RecipeView[] =>
      COOKBOOK_RECIPES.map((recipe, index) => {
        const name = recipe[hanziScript];
        // oxlint-disable-next-line typescript/no-misused-spread -- Catalog names contain individual Han code points, matching study keys.
        const characters = [...name].filter(
          (character, index, all) => all.indexOf(character) === index,
        );
        const studied = characters.filter((character) => studiedCharacters?.has(character)).length;
        const unlocked =
          !isError && studiedCharacters !== undefined && studied === characters.length;
        const progress = isError
          ? "Progress unavailable"
          : studiedCharacters === undefined
            ? "Loading progress…"
            : unlocked
              ? "Unlocked"
              : `Locked · ${studied}/${characters.length} characters learned`;
        const needed =
          isError || studiedCharacters === undefined || unlocked
            ? null
            : `Still needed: ${characters.filter((character) => !studiedCharacters.has(character)).join(" · ")}`;
        return { ...recipe, name, needed, page: index + 1, progress, unlocked };
      }),
    [hanziScript, isError, studiedCharacters],
  );
  if (recipeId === undefined) {
    const unlockedCount = recipes.filter((recipe) => recipe.unlocked).length;
    const progress = isError
      ? "Learning progress is unavailable right now."
      : studiedCharacters === undefined
        ? "Loading learning progress…"
        : `${unlockedCount} of ${recipes.length} recipes unlocked · ${recipes.length - unlockedCount} still to unlock`;
    return <CookbookContents progress={progress} recipes={recipes} />;
  }
  const selected = recipes.find((recipe) => recipe.id === recipeId);
  return (
    <View className="flex-1 bg-background">
      <PrimaryControlsStripToolbar floating title="Cookbook" />
      <FrostedBackdropTarget className="flex-1">
        <HeaderScrollView
          className="flex-1 bg-background"
          contentContainerClassName="px-5 pt-6 pb-10"
          glassHeaderOverlap={toolbarHeight}
          testID="cookbook-recipe-page"
        >
          <PrimaryControlsStripTitle>Cookbook</PrimaryControlsStripTitle>
          <PrimaryControlsStripBack
            appShellPlacement="toolbar"
            fallbackHref="/(nav)/(app)/cookbook"
          >
            Cookbook
          </PrimaryControlsStripBack>
          <View className="mx-auto w-full max-w-2xl gap-6">
            {selected === undefined ? (
              <View className="gap-4 py-8">
                <Text className="text-2xl font-semibold text-foreground">Recipe not found</Text>
                <CookbookContentsLink />
              </View>
            ) : (
              <View className="gap-8 rounded-xs border border-border bg-background p-6">
                <Text className="text-xs tracking-widest text-muted-foreground uppercase">
                  {selected.chapter} · Page {selected.page}
                </Text>
                <View className="gap-2">
                  <Text accessibilityRole="header" className="text-4xl font-bold text-foreground">
                    {selected.name}
                  </Text>
                  <Text className="text-xl text-muted-foreground">{selected.english}</Text>
                  <Text className="text-sm text-muted-foreground">{selected.progress}</Text>
                  {selected.needed === null ? null : (
                    <Text className="text-base text-foreground">{selected.needed}</Text>
                  )}
                </View>
                <View className="items-center gap-3 border-y border-border py-16">
                  <Icon className="text-muted-foreground" name="MdMenuBook" size={40} />
                  <Text className="text-3xl font-semibold text-foreground">Coming Soon!</Text>
                  <Text className="text-center text-sm text-muted-foreground">
                    Something delicious is on its way.
                  </Text>
                </View>
                <CookbookContentsLink />
              </View>
            )}
          </View>
        </HeaderScrollView>
      </FrostedBackdropTarget>
    </View>
  );
}

function CookbookContentsLink() {
  return (
    <Link asChild dismissTo href="/(nav)/(app)/cookbook">
      <Pressable
        accessibilityRole="link"
        className="min-h-12 justify-center rounded-xs bg-muted px-4"
      >
        <Text className="text-center font-semibold text-foreground">Back to table of contents</Text>
      </Pressable>
    </Link>
  );
}

export function CookbookPage({ recipeId }: { recipeId?: string }) {
  return (
    <CookbookAccessGate>
      <SuspensePageBoundary fallback={<DocumentLayoutSkeleton />} location={import.meta.location}>
        <CookbookContent recipeId={recipeId} />
      </SuspensePageBoundary>
    </CookbookAccessGate>
  );
}
